import React, { useState } from "react";
import { X as XIcon } from "lucide-react";

/**
 * One-time, dismissible notice shown ONLY to users running RockLink as an
 * installed home-screen app on iOS (navigator.standalone === true). iOS keeps
 * the icon captured at install time and won't auto-refresh it, so we suggest
 * removing + re-adding the app to pick up the new RL² icon. Dismissal is
 * remembered in localStorage so it never nags. Renders nothing elsewhere.
 */
export default function IconUpdateNotice() {
  const isIosStandalone =
    typeof navigator !== "undefined" &&
    // iOS Safari exposes this only when launched from the home screen
    navigator.standalone === true &&
    /iPad|iPhone|iPod/.test(navigator.userAgent || "");

  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("rl_icon_v2_notice_dismissed") === "1"; }
    catch { return false; }
  });

  if (!isIosStandalone || dismissed) return null;

  const close = () => {
    try { localStorage.setItem("rl_icon_v2_notice_dismissed", "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="fixed bottom-4 inset-x-4 z-[120] mx-auto max-w-md rounded-2xl px-4 py-3 flex items-start gap-3 rl-frost border-[hsl(var(--border))]"
      role="status"
      data-testid="icon-update-notice"
    >
      <div className="flex-1 text-sm text-[hsl(var(--foreground))]">
        <p className="font-semibold">New app icon available</p>
        <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug">
          To get the new RockLink icon on your home screen, remove this app and
          add it again from Safari.
        </p>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        className="shrink-0 -mr-1 -mt-1 p-1.5 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/60 transition-colors"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
